package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.animation.AnimationSetup
import dev.danko.roblox_craft_ai.shared.animation.CombatMode
import dev.danko.roblox_craft_ai.shared.model.ModelImportKind
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import org.w3c.files.FileList
import org.w3c.files.get

/** One of the user's 3D works as the model window shows it. [riggedUrl] is set once it has bones. */
class LibraryModel(
    val assetId: String,
    val title: String,
    val image: String?,
    val glbUrl: String,
    val riggedUrl: String?,
    val prompt: String
)

/** What the model window works with: the user's models and what it can do with them (from the page). */
class ModelLibrary(
    val models: List<LibraryModel>,
    /** Uploads own files as a new work (with a picture; a character also gets bones); throws with a readable message. */
    val importFiles: suspend (files: List<File>, kind: ModelImportKind) -> LibraryModel,
    /** Puts the template skeleton into a work; throws with a readable message. */
    val addBones: suspend (assetId: String) -> LibraryModel,
    /** Opens the work with all its tools (polygons, textures, bones). */
    val openWork: (assetId: String) -> Unit
)

private const val UPLOAD_INPUT_ID = "rc-model-upload"

/**
 * «Модель персонажа и оружия»: picks the character and the weapon model from the user's works, uploads own files,
 * puts bones into a character, leads to the work's tools. Choices go straight into [AnimationSetupPrefs].
 */
@Composable
fun ModelLibraryDialog(library: ModelLibrary, startWithWeapon: Boolean, onClose: () -> Unit) {
    val a = strings().animationSetup
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    val setup = AnimationSetupPrefs.setup
    var weaponTab by remember { mutableStateOf(startWithWeapon) }
    var busy by remember { mutableStateOf<String?>(null) }
    var dragOver by remember { mutableStateOf(false) }
    // A character picked without bones waits here for «Поставить кости».
    var pending by remember { mutableStateOf<LibraryModel?>(null) }

    fun run(label: String, block: suspend () -> Unit) {
        if (busy != null) return
        busy = label
        scope.launch {
            try {
                block()
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: a.libraryFailed)
            } finally {
                busy = null
            }
        }
    }

    fun chooseCharacter(model: LibraryModel?) {
        pending = null
        if (model == null) AnimationSetupPrefs.update { it.copy(characterUrl = null) }
        else if (model.riggedUrl != null) AnimationSetupPrefs.update { it.copy(characterUrl = model.riggedUrl) }
        else pending = model
    }

    fun chooseWeapon(model: LibraryModel?) {
        AnimationSetupPrefs.update {
            val mode = if (model != null && (it.mode == CombatMode.NONE || it.mode == CombatMode.FISTS)) CombatMode.ONE_WEAPON else it.mode
            val kind = model?.let { m -> AnimationSetup.guessKind(m.title + " " + m.prompt) } ?: it.weapon
            it.copy(weaponModelUrl = model?.glbUrl, weaponName = model?.title, weapon = kind, mode = mode)
        }
    }

    fun upload(files: List<File>) {
        if (files.isEmpty()) return
        val kind = if (weaponTab) ModelImportKind.WEAPON else ModelImportKind.CHARACTER
        run(a.libraryUploading) {
            val model = library.importFiles(files, kind)
            ToastManager.success(a.libraryImported(model.title))
            if (weaponTab) chooseWeapon(model) else chooseCharacter(model)
        }
    }

    fun filesOf(list: FileList?): List<File> = if (list == null) emptyList() else (0 until list.length).mapNotNull { list[it] }

    ModalDialog(visible = true, onCloseRequest = onClose, title = a.libraryTitle, maxWidthRem = 46.0, footer = {
        Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.flexGrow(1))
            AppButton(text = a.libraryDone, onClick = onClose)
        }
    }) {
        Column(Modifier.fillMaxWidth().gap(12.px)) {
            Row(Modifier.gap(6.px)) {
                AppButton(text = a.characterTitle, icon = AppIcon.FIGURE, size = AppButtonSize.SM,
                    variant = if (!weaponTab) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE, onClick = { weaponTab = false })
                AppButton(text = a.weaponModelTitle, icon = AppIcon.ZAP, size = AppButtonSize.SM,
                    variant = if (weaponTab) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE, onClick = { weaponTab = true })
            }
            SpanText(if (weaponTab) a.libraryWeaponHint else a.libraryCharacterHint, Modifier.fontSize(12.px).color(palette.textSecondary))

            Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
                if (weaponTab) {
                    ChoiceCard(a.weaponModelBuiltIn, null, AppIcon.ZAP, a.weaponModelBuiltInHint, setup.weaponModelUrl == null, busy == null) { chooseWeapon(null) }
                    library.models.forEach { model ->
                        ChoiceCard(model.title, model.image, AppIcon.CUBE, model.prompt, setup.weaponModelUrl == model.glbUrl, busy == null) { chooseWeapon(model) }
                    }
                } else {
                    ChoiceCard(a.characterR15, null, AppIcon.FIGURE, a.characterR15Hint, setup.characterUrl == null && pending == null, busy == null) { chooseCharacter(null) }
                    library.models.forEach { model ->
                        val chosen = (model.riggedUrl != null && setup.characterUrl == model.riggedUrl) || pending?.assetId == model.assetId
                        val label = if (model.riggedUrl == null) "${model.title} · ${a.libraryNoBones}" else model.title
                        ChoiceCard(label, model.image, AppIcon.CUBE, model.prompt, chosen, busy == null) { chooseCharacter(model) }
                    }
                }
            }
            if (library.models.isEmpty()) SpanText(a.libraryEmpty, Modifier.fontSize(12.px).color(palette.textSecondary))

            // The chosen model: bones for a character, its tools, download.
            val chosen = if (weaponTab) library.models.firstOrNull { it.glbUrl == setup.weaponModelUrl }
                else pending ?: library.models.firstOrNull { it.riggedUrl != null && it.riggedUrl == setup.characterUrl }
            if (chosen != null) {
                Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap).padding(10.px).borderRadius(10.px)
                    .backgroundColor(palette.background).border(1.px, LineStyle.Solid, palette.border), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(chosen.title, Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(palette.textPrimary).flexGrow(1))
                    if (!weaponTab && chosen.riggedUrl == null) {
                        AppButton(text = if (busy == a.libraryAddingBones) a.libraryAddingBones else a.libraryAddBones, icon = AppIcon.FIGURE, size = AppButtonSize.SM,
                            loading = busy == a.libraryAddingBones, enabled = busy == null, onClick = {
                                run(a.libraryAddingBones) {
                                    val rigged = library.addBones(chosen.assetId)
                                    pending = null
                                    rigged.riggedUrl?.let { url -> AnimationSetupPrefs.update { it.copy(characterUrl = url) } }
                                }
                            })
                    }
                    AppButton(text = a.libraryTools, icon = AppIcon.SLIDERS, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM,
                        enabled = busy == null, onClick = { onClose(); library.openWork(chosen.assetId) })
                    A(href = BackendEndpoint.resolveAssetUrl(chosen.glbUrl), attrs = Modifier.textDecorationLine(com.varabyte.kobweb.compose.css.TextDecorationLine.None).toAttrs {
                        attr("download", "${chosen.title}.glb")
                    }) {
                        AppButton(text = a.libraryDownload, icon = AppIcon.DOWNLOAD, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, onClick = {})
                    }
                }
            }

            // Own files: a click on the zone or files dropped onto it.
            Div(Modifier.fillMaxWidth().padding(16.px).borderRadius(12.px)
                .border(2.px, LineStyle.Dashed, if (dragOver) palette.accent else palette.borderStrong)
                .backgroundColor(if (dragOver) palette.accent.toRgb().copy(alpha = 30) else palette.background)
                .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                .toAttrs {
                    attr("role", "button")
                    attr("tabindex", "0")
                    attr("aria-label", a.libraryUpload)
                    onClick { if (busy == null) (document.getElementById(UPLOAD_INPUT_ID) as? HTMLInputElement)?.click() }
                    onKeyDown { if (it.key == "Enter" || it.key == " ") (document.getElementById(UPLOAD_INPUT_ID) as? HTMLInputElement)?.click() }
                    // Stopped here: the prompt box around takes dropped pictures as references.
                    onDragEnter { it.preventDefault(); it.stopPropagation(); dragOver = true }
                    onDragOver { it.preventDefault(); it.stopPropagation() }
                    onDragLeave { it.stopPropagation(); dragOver = false }
                    onDrop { event ->
                        event.preventDefault()
                        event.stopPropagation()
                        dragOver = false
                        if (busy == null) upload(filesOf(event.nativeEvent.asDynamic().dataTransfer?.files.unsafeCast<FileList?>()))
                    }
                }) {
                Column(Modifier.fillMaxWidth().gap(4.px), horizontalAlignment = Alignment.CenterHorizontally) {
                    Box(Modifier.color(palette.accent)) { Icon(AppIcon.DOWNLOAD, size = 22) }
                    SpanText(if (busy == a.libraryUploading) a.libraryUploading else a.libraryUpload,
                        Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(palette.textPrimary))
                    SpanText(a.libraryUploadHint, Modifier.fontSize(12.px).color(palette.textSecondary).styleModifier { property("text-align", "center") })
                }
            }
            Input(type = InputType.File, attrs = Modifier.display(DisplayStyle.None).id(UPLOAD_INPUT_ID).toAttrs {
                attr("accept", ".glb,.obj,.mtl,.png,.jpg,.jpeg,.webp,.tga,.bmp")
                attr("multiple", "true")
                onInput { event ->
                    val target = event.target as? HTMLInputElement
                    upload(filesOf(target?.files))
                    target?.value = ""
                }
            })
        }
    }
}
